<template>
  <div class="migration-management p-4">
    <admin-nav />
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-database me-2"></i>
        Database Migration Management
      </h2>
      <button class="btn btn-primary" @click="refreshStatus">
        <i class="fas fa-sync-alt me-2"></i>
        Refresh
      </button>
    </div>

    <!-- Tenant Filter -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-4">
            <label class="form-label fw-bold">Select Tenant/Schema</label>
            <select class="form-select" v-model="selectedTenant" @change="onTenantChange">
              <option value="">-- All Tenants Overview --</option>
              <option v-for="tenant in tenantMigrations" :key="tenant.SchemaName" :value="tenant.SchemaName">
                {{ tenant.TenantName }} ({{ tenant.SchemaName }})
              </option>
            </select>
          </div>
          <div class="col-md-8" v-if="selectedTenantStatus">
            <div class="d-flex gap-4 mt-3 mt-md-0">
              <div class="text-center">
                <div class="fs-4 fw-bold">{{ selectedTenantStatus.TotalMigrations }}</div>
                <small class="text-muted">Total</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold text-success">{{ selectedTenantStatus.AppliedMigrations }}</div>
                <small class="text-muted">Applied</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold text-warning">{{ selectedTenantStatus.PendingMigrations }}</div>
                <small class="text-muted">Pending</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- All Tenants Overview (when no tenant selected) -->
    <div class="card mb-4" v-if="!selectedTenant && tenantMigrations.length > 0">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">All Tenants Migration Status</h5>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Schema</th>
                <th>Total</th>
                <th>Applied</th>
                <th>Pending</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tenant in tenantMigrations" :key="tenant.SchemaName" 
                  @click="selectedTenant = tenant.SchemaName; onTenantChange()"
                  style="cursor: pointer;">
                <td><strong>{{ tenant.TenantName }}</strong></td>
                <td><code>{{ tenant.SchemaName }}</code></td>
                <td>{{ tenant.TotalMigrations }}</td>
                <td class="text-success">{{ tenant.AppliedMigrations }}</td>
                <td class="text-warning">{{ tenant.PendingMigrations }}</td>
                <td>
                  <span class="badge" :class="tenant.PendingMigrations === 0 ? 'bg-success' : 'bg-warning'">
                    {{ tenant.PendingMigrations === 0 ? 'Up to date' : 'Needs update' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Migrations for Selected Tenant -->
    <div v-if="selectedTenant && selectedTenantStatus">
      <!-- Pending Migrations for Tenant -->
      <div class="card mb-4" v-if="tenantPendingMigrations.length > 0">
        <div class="card-header bg-warning">
          <h5 class="mb-0">
            <i class="fas fa-clock me-2"></i>
            Pending Migrations for {{ selectedTenantStatus.TenantName }} ({{ tenantPendingMigrations.length }})
          </h5>
        </div>
        <div class="card-body">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle me-2"></i>
            These migrations have not been applied to this tenant's schema yet.
          </div>
          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="migration in tenantPendingMigrations" :key="migration.Version">
                  <td><code>{{ migration.Version }}</code></td>
                  <td>{{ migration.Description }}</td>
                  <td>
                    <button 
                      class="btn btn-sm btn-success me-2" 
                      @click="executeMigration(migration.Version, selectedTenant)"
                      :disabled="executing">
                      <i class="fas fa-play me-1"></i>
                      Execute
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button 
            class="btn btn-primary mt-3" 
            @click="executeAllPending"
            :disabled="executing">
            <i class="fas fa-forward me-2"></i>
            Execute All Pending for This Tenant
          </button>
        </div>
      </div>

      <!-- Applied Migrations for Tenant -->
      <div class="card">
        <div class="card-header bg-success text-white">
          <h5 class="mb-0">
            <i class="fas fa-check me-2"></i>
            Applied Migrations for {{ selectedTenantStatus.TenantName }} ({{ tenantAppliedMigrations.length }})
          </h5>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Applied On</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="migration in tenantAppliedMigrations" :key="migration.Version">
                  <td><code>{{ migration.Version }}</code></td>
                  <td>{{ migration.Description }}</td>
                  <td>{{ formatDate(migration.AppliedOn) }}</td>
                  <td>
                    <span class="badge bg-success">Applied</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Pending Migrations (when no tenant selected) -->
    <div class="card mb-4" v-if="!selectedTenant && pendingMigrations.length > 0">
      <div class="card-header bg-warning">
        <h5 class="mb-0">
          <i class="fas fa-clock me-2"></i>
          Global Pending Migrations ({{ pendingMigrations.length }})
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          These migrations are pending on one or more schemas. Running "Execute All" will only apply migrations to schemas where they haven't been run yet.
        </div>
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Version</th>
                <th>Description</th>
                <th>Pending On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="migration in pendingMigrations" :key="migration.Version">
                <td><code>{{ migration.Version }}</code></td>
                <td>{{ migration.Description }}</td>
                <td>
                  <span v-for="schema in migration.PendingOnSchemas" :key="schema" class="badge bg-secondary me-1">
                    {{ schema }}
                  </span>
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <button 
                      class="btn btn-sm btn-outline-warning" 
                      @click="executeMigration(migration.Version, 'dev')"
                      :disabled="executing"
                      title="Run only on dev schema (safe for testing)">
                      <i class="fas fa-flask me-1"></i>
                      Dev Only
                    </button>
                    <button 
                      class="btn btn-sm btn-success" 
                      @click="executeMigration(migration.Version)"
                      :disabled="executing"
                      title="Run on all schemas where pending">
                      <i class="fas fa-play me-1"></i>
                      All Schemas
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex gap-2 mt-3">
          <button 
            class="btn btn-outline-warning" 
            @click="executeAllPendingDevOnly"
            :disabled="executing"
            title="Run all pending migrations only on dev schema (safe for testing)">
            <i class="fas fa-flask me-2"></i>
            Execute All (Dev Only)
          </button>
          <button 
            class="btn btn-primary" 
            @click="executeAllPending"
            :disabled="executing"
            title="Run all pending migrations on all schemas">
            <i class="fas fa-forward me-2"></i>
            Execute All (All Schemas)
          </button>
        </div>
      </div>
    </div>

    <!-- Execution Results Modal -->
    <div class="modal" :class="{ 'show d-block': showResultsModal }" v-if="showResultsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Migration Execution Results</h5>
            <button type="button" class="btn-close" @click="closeResultsModal"></button>
          </div>
          <div class="modal-body">
            <div v-for="(result, index) in executionResults" :key="index" 
                 class="alert mb-2"
                 :class="result.Success ? 'alert-success' : 'alert-danger'">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Version {{ result.Version }}</strong>
                  <span v-if="result.SchemaName"> on {{ result.SchemaName }}</span>
                  <p class="mb-0 mt-1">{{ result.Message }}</p>
                  <small v-if="result.Duration">
                    Duration: {{ formatDuration(result.Duration) }}
                  </small>
                </div>
                <i :class="result.Success ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"></i>
              </div>
              <div v-if="result.ErrorDetails" class="mt-2">
                <details>
                  <summary>Error Details</summary>
                  <pre class="mt-2 small">{{ result.ErrorDetails }}</pre>
                </details>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeResultsModal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showResultsModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Http from '@/services/Http';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import AdminNav from '@/components/Shared/AdminNav.vue';

const statusSummary = ref<any>(null);
const allMigrations = ref<any[]>([]);
const appliedMigrations = ref<any[]>([]);
const pendingMigrations = ref<any[]>([]);
const tenantMigrations = ref<any[]>([]);
const selectedTenant = ref<string>('');
const executing = ref(false);
const showResultsModal = ref(false);
const executionResults = ref<any[]>([]);

// Computed properties for selected tenant
const selectedTenantStatus = computed(() => {
  if (!selectedTenant.value) return null;
  return tenantMigrations.value.find(t => t.SchemaName === selectedTenant.value);
});

const tenantAppliedMigrations = computed(() => {
  if (!selectedTenantStatus.value) return [];
  return selectedTenantStatus.value.Migrations?.filter((m: any) => m.IsApplied) || [];
});

const tenantPendingMigrations = computed(() => {
  if (!selectedTenantStatus.value) return [];
  return selectedTenantStatus.value.Migrations?.filter((m: any) => !m.IsApplied) || [];
});

const onTenantChange = () => {
  // Tenant selection changed - computed properties will update automatically
};

const refreshStatus = async () => {
  try {
    // Get tenant migrations (includes per-tenant status)
    tenantMigrations.value = await ApiService.loadTenantMigrations(true);

    // Get status summary
    const statusResponse = await Http.get('/admin/migrations/status');
    statusSummary.value = statusResponse;

    // Get all migrations
    const allResponse = await Http.get('/admin/migrations/all');
    allMigrations.value = allResponse;

    // Get applied migrations (for dbo schema by default)
    const appliedResponse = await Http.get('/admin/migrations/applied');
    appliedMigrations.value = appliedResponse?.sort((a: any, b: any) => b.Version - a.Version) || [];

    // Get pending migrations
    const pendingResponse = await Http.get('/admin/migrations/pending');
    pendingMigrations.value = pendingResponse?.sort((a: any, b: any) => a.Version - b.Version) || [];
  } catch (error: any) {
    console.error('Error refreshing migration status:', error);
    Alert.notify({ text: 'Error loading migration status: ' + (error.response?.data?.error || error.message), type: 'error' });
  }
};

const executeMigration = async (version: number, schema?: string) => {
  const targetMsg = schema ? `schema ${schema}` : 'all active tenant schemas';
  if (!confirm(`Execute migration ${version}? This will run on ${targetMsg}.`)) {
    return;
  }

  executing.value = true;
  try {
    const url = schema 
      ? `/admin/migrations/execute/${version}?schema=${schema}`
      : `/admin/migrations/execute/${version}`;
    const response = await Http.post(url);
    executionResults.value = [response];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error executing migration:', error);
    Alert.notify({ text: 'Error executing migration: ' + (error.response?.data?.error || error.message), type: 'error' });
  } finally {
    executing.value = false;
  }
};

const executeAllPending = async () => {
  const targetMsg = selectedTenant.value 
    ? `Execute all pending migrations for schema "${selectedTenant.value}"?`
    : `Execute ALL ${pendingMigrations.value.length} pending migrations across all schemas (dbo, dev, and any active tenants)? Each migration will only run on schemas where it hasn't been applied yet.`;
  
  if (!confirm(targetMsg)) {
    return;
  }

  executing.value = true;
  try {
    // Use tenant-specific endpoint if a tenant is selected
    const url = selectedTenant.value 
      ? `/admin/migrations/execute-all/${selectedTenant.value}`
      : '/admin/migrations/execute-all';
    const response = await Http.post(url);
    executionResults.value = Array.isArray(response) ? response : [response];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error executing all migrations:', error);
    const errorData = error.response?.data;
    if (errorData && errorData.results) {
      executionResults.value = errorData.results;
      showResultsModal.value = true;
    } else {
      Alert.notify({ text: 'Error executing migrations: ' + (errorData?.error || error.message), type: 'error' });
    }
  } finally {
    executing.value = false;
  }
};

const executeAllPendingDevOnly = async () => {
  if (!confirm(`Execute all pending migrations on DEV schema only? This is safe for testing before deploying to production schemas.`)) {
    return;
  }

  executing.value = true;
  try {
    const response = await Http.post('/admin/migrations/execute-all/dev');
    executionResults.value = Array.isArray(response) ? response : [response];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error executing dev migrations:', error);
    const errorData = error.response?.data;
    if (errorData && errorData.results) {
      executionResults.value = errorData.results;
      showResultsModal.value = true;
    } else {
      Alert.notify({ text: 'Error executing migrations: ' + (errorData?.error || error.message), type: 'error' });
    }
  } finally {
    executing.value = false;
  }
};

const confirmRollback = async (version: number) => {
  if (!confirm(`⚠️ WARNING: Rollback migration ${version}? This is a destructive operation that cannot be undone easily!`)) {
    return;
  }

  if (!confirm('Are you absolutely sure? This will affect all tenant schemas.')) {
    return;
  }

  executing.value = true;
  try {
    const response = await Http.post(`/admin/migrations/rollback/${version}`);
    executionResults.value = [response];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error rolling back migration:', error);
    Alert.notify({ text: 'Error rolling back migration: ' + (error.response?.data?.error || error.message), type: 'error' });
  } finally {
    executing.value = false;
  }
};

const closeResultsModal = () => {
  showResultsModal.value = false;
  executionResults.value = [];
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const formatDuration = (duration: any) => {
  if (typeof duration === 'string') {
    return duration;
  }
  // Duration is likely a TimeSpan object
  return `${duration.TotalSeconds?.toFixed(2) || '0'}s`;
};

onMounted(() => {
  refreshStatus();
});
</script>

<style scoped>
.migration-management {
  max-width: 1400px;
  margin: 0 auto;
}

.stat-card {
  text-align: center;
  padding: 1rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
}

.table code {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

details {
  cursor: pointer;
}

details summary {
  color: #0d6efd;
}

details pre {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 0.25rem;
  overflow-x: auto;
}
</style>
